// =====================================================
// ENUMS
// =====================================================

export type AccountClass = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export type AccountNature = 'debit' | 'credit' | 'both';

export type JournalType = 
  | 'general'
  | 'sales'
  | 'purchases'
  | 'bank'
  | 'cash'
  | 'payroll'
  | 'operations';

export type EntryStatus = 'draft' | 'posted' | 'cancelled';

export type FiscalYearStatus = 'open' | 'closed' | 'archived';

export type ReferenceType = 'invoice' | 'transaction' | 'payroll' | 'manual';

export type ThirdPartyType = 'client' | 'supplier' | 'employee';

// =====================================================
// CHART OF ACCOUNTS
// =====================================================

export interface ChartOfAccount {
  id: string;
  company_id: string;
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  parent_code?: string | null;
  account_class: AccountClass;
  account_type: AccountType;
  nature?: AccountNature | null;
  opening_balance: number;
  current_balance: number;
  is_active: boolean;
  is_system_account: boolean;
  description?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface CreateChartOfAccountInput {
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  parent_code?: string | null;
  account_class: AccountClass;
  account_type: AccountType;
  nature?: AccountNature | null;
  opening_balance?: number;
  description?: string | null;
}

export interface UpdateChartOfAccountInput {
  account_name?: string;
  account_name_ar?: string | null;
  is_active?: boolean;
  description?: string | null;
}

// =====================================================
// FISCAL YEARS
// =====================================================

export interface FiscalYear {
  id: string;
  company_id: string;
  year_code: string;
  start_date: string;
  end_date: string;
  status: FiscalYearStatus;
  is_locked: boolean;
  lock_date?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateFiscalYearInput {
  year_code: string;
  start_date: string;
  end_date: string;
}

export interface UpdateFiscalYearInput {
  status?: FiscalYearStatus;
  is_locked?: boolean;
  lock_date?: string | null;
}

// =====================================================
// JOURNAL ENTRIES
// =====================================================

export interface JournalEntry {
  id: string;
  company_id: string;
  fiscal_year_id: string;
  entry_number: string;
  entry_date: string;
  journal_type: JournalType;
  status: EntryStatus;
  reference_type?: ReferenceType | null;
  reference_id?: string | null;
  description?: string | null;
  total_debit: number;
  total_credit: number;
  is_balanced: boolean;
  posted_at?: string | null;
  posted_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface JournalEntryLine {
  id: string;
  entry_id: string;
  line_number: number;
  account_id: string;
  account_code: string;
  debit: number;
  credit: number;
  description?: string | null;
  third_party_type?: ThirdPartyType | null;
  third_party_id?: string | null;
  is_reconciled: boolean;
  reconciled_at?: string | null;
  created_at: string;
}

export interface CreateJournalEntryInput {
  fiscal_year_id: string;
  entry_date: string;
  journal_type: JournalType;
  reference_type?: ReferenceType | null;
  reference_id?: string | null;
  description?: string | null;
  lines: CreateJournalEntryLineInput[];
}

export interface CreateJournalEntryLineInput {
  account_code: string;
  debit: number;
  credit: number;
  description?: string | null;
  third_party_type?: ThirdPartyType | null;
  third_party_id?: string | null;
}

export interface UpdateJournalEntryInput {
  entry_date?: string;
  description?: string | null;
  lines?: CreateJournalEntryLineInput[];
}

// =====================================================
// REPORTS
// =====================================================

export interface TrialBalance {
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  opening_debit: number;
  opening_credit: number;
  period_debit: number;
  period_credit: number;
  closing_debit: number;
  closing_credit: number;
}

export interface GeneralLedger {
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  entries: GeneralLedgerEntry[];
}

export interface GeneralLedgerEntry {
  entry_date: string;
  entry_number: string;
  description?: string | null;
  debit: number;
  credit: number;
  balance: number;
}

export interface BalanceSheet {
  assets: BalanceSheetSection[];
  liabilities: BalanceSheetSection[];
  equity: BalanceSheetSection[];
  total_assets: number;
  total_liabilities: number;
  total_equity: number;
}

export interface BalanceSheetSection {
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  amount: number;
  children?: BalanceSheetSection[];
}

export interface IncomeStatement {
  revenue: IncomeStatementSection[];
  expenses: IncomeStatementSection[];
  total_revenue: number;
  total_expenses: number;
  net_income: number;
}

export interface IncomeStatementSection {
  account_code: string;
  account_name: string;
  account_name_ar?: string | null;
  amount: number;
  children?: IncomeStatementSection[];
}

// =====================================================
// ALGERIAN TAX REPORTS
// =====================================================

export interface TVADeclarationG50 {
  period: string;
  sales_ht: number;
  sales_tva: number;
  purchases_ht: number;
  purchases_tva: number;
  tva_collectee: number;
  tva_deductible: number;
  tva_due: number;
  tva_credit?: number;
}

export interface TaxReport {
  report_type: 'IBS' | 'TAP' | 'IRG' | 'FISCAL_STAMP';
  period_start: string;
  period_end: string;
  total_amount: number;
  details: TaxReportDetail[];
}

export interface TaxReportDetail {
  account_code: string;
  account_name: string;
  amount: number;
}

// =====================================================
// FILTERS & UTILITIES
// =====================================================

export interface AccountFilter {
  accountClass?: AccountClass;
  accountType?: AccountType;
  isActive?: boolean;
  search?: string;
}

export interface EntryFilter {
  fiscalYearId?: string;
  journalType?: JournalType;
  status?: EntryStatus;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface AccountHierarchy extends ChartOfAccount {
  level: number;
  children: AccountHierarchy[];
}

export interface JournalEntryWithLines extends JournalEntry {
  lines: JournalEntryLine[];
}

// =====================================================
// LABELS (Bilingual)
// =====================================================

export const ACCOUNT_CLASS_LABELS: Record<AccountClass, { fr: string; ar: string }> = {
  1: { fr: 'Capitaux', ar: 'رأس المال' },
  2: { fr: 'Immobilisations', ar: 'الأصول الثابتة' },
  3: { fr: 'Stocks', ar: 'المخزون' },
  4: { fr: 'Tiers', ar: 'الغير' },
  5: { fr: 'Financiers', ar: 'المالية' },
  6: { fr: 'Charges', ar: 'المصروفات' },
  7: { fr: 'Produits', ar: 'الإيرادات' },
  8: { fr: 'Spéciaux', ar: 'خاصة' },
  9: { fr: 'Analytiques', ar: 'تحليلية' },
};

export const JOURNAL_TYPE_LABELS: Record<JournalType, { fr: string; ar: string }> = {
  general: { fr: 'Général', ar: 'عام' },
  sales: { fr: 'Ventes', ar: 'مبيعات' },
  purchases: { fr: 'Achats', ar: 'مشتريات' },
  bank: { fr: 'Banque', ar: 'بنك' },
  cash: { fr: 'Caisse', ar: 'صندوق' },
  payroll: { fr: 'Paie', ar: 'رواتب' },
  operations: { fr: 'Opérations Diverses', ar: 'عمليات متنوعة' },
};
